/*
 * ACESO — ortak davranışlar
 *
 * Tüm bileşenler özel element (custom element) olarak yazılmıştır; tema düzenleyicide
 * bir bölüm yeniden çizildiğinde tarayıcı eski elementi söker, yenisini kendiliğinden
 * kurar. Sayfaya özel davranışlar product.js ve collection.js dosyalarındadır.
 */
(() => {
  const theme = (window.theme = window.theme || {});
  const root = document.documentElement;

  /* ------------------------------------------------------------------ */
  /* Yardımcılar                                                          */
  /* ------------------------------------------------------------------ */

  theme.prefersReducedMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  theme.debounce = (fn, wait = 200) => {
    let timer;
    return (...args) => {
      window.clearTimeout(timer);
      timer = window.setTimeout(() => fn(...args), wait);
    };
  };

  /** Ekran okuyuculara kısa bir durum mesajı iletir. */
  theme.announce = (message) => {
    const region = document.getElementById('live-region');
    if (!region) return;
    region.textContent = '';
    window.requestAnimationFrame(() => {
      region.textContent = message;
    });
  };

  /** Seçenek değeri kendi içinde "beden" ya da "size" sözcüğünü taşıyor mu? */
  theme.mentionsSize = (value) => /beden|size/i.test(String(value || ''));

  /** Kuruş cinsinden tutarı mağazanın para biçimiyle yazar. */
  theme.formatMoney = (cents, format = theme.settings.moneyFormat) => {
    const value = typeof cents === 'string' ? Number(cents.replace('.', '')) : cents;
    const withDelimiters = (number, precision, thousands, decimal) => {
      if (Number.isNaN(number) || number == null) return '0';
      const fixed = (number / 100).toFixed(precision);
      const [whole, fraction] = fixed.split('.');
      const grouped = whole.replace(/(\d)(?=(\d\d\d)+(?!\d))/g, `$1${thousands}`);
      return fraction ? grouped + decimal + fraction : grouped;
    };
    const match = format.match(/\{\{\s*(\w+)\s*\}\}/);
    let amount;
    switch (match ? match[1] : 'amount') {
      case 'amount_no_decimals':
        amount = withDelimiters(value, 0, ',', '.');
        break;
      case 'amount_with_comma_separator':
        amount = withDelimiters(value, 2, '.', ',');
        break;
      case 'amount_no_decimals_with_comma_separator':
        amount = withDelimiters(value, 0, '.', ',');
        break;
      case 'amount_with_apostrophe_separator':
        amount = withDelimiters(value, 2, "'", '.');
        break;
      case 'amount_no_decimals_with_space_separator':
        amount = withDelimiters(value, 0, ' ', '.');
        break;
      case 'amount_with_space_separator':
        amount = withDelimiters(value, 2, ' ', ',');
        break;
      case 'amount_with_period_and_space_separator':
        amount = withDelimiters(value, 2, ' ', '.');
        break;
      default:
        amount = withDelimiters(value, 2, ',', '.');
    }
    return format.replace(/\{\{\s*\w+\s*\}\}/, amount);
  };

  /** Bir adresi indirip ayrıştırılmış belge olarak döner. */
  theme.fetchDocument = async (url, options) => {
    const response = await fetch(url, options);
    if (!response.ok) throw new Error(`Request failed: ${response.status}`);
    return new DOMParser().parseFromString(await response.text(), 'text/html');
  };

  /** Tek bir ürünün kartını Section Rendering API ile getirir. */
  theme.fetchProductCard = async (handle) => {
    const url = `${theme.routes.root.replace(/\/$/, '')}/products/${encodeURIComponent(handle)}?section_id=product-card`;
    try {
      const doc = await theme.fetchDocument(url);
      return doc.querySelector('product-card');
    } catch (error) {
      return null;
    }
  };

  const storage = {
    read(key, fallback) {
      try {
        const value = JSON.parse(window.localStorage.getItem(key));
        return value == null ? fallback : value;
      } catch (error) {
        return fallback;
      }
    },
    write(key, value) {
      try {
        window.localStorage.setItem(key, JSON.stringify(value));
      } catch (error) {
        /* Depolama kapalıysa durum yalnızca bu sayfa için geçerli kalır. */
      }
    },
  };
  theme.storage = storage;

  /* ------------------------------------------------------------------ */
  /* Çekmece ve pencere iskeleti                                          */
  /* ------------------------------------------------------------------ */

  let openOverlayCount = 0;

  const lockScroll = () => {
    if (openOverlayCount === 0) {
      root.style.setProperty('--scrollbar-width', `${window.innerWidth - root.clientWidth}px`);
      root.classList.add('has-open-overlay');
    }
    openOverlayCount += 1;
  };

  const unlockScroll = () => {
    openOverlayCount = Math.max(0, openOverlayCount - 1);
    if (openOverlayCount === 0) root.classList.remove('has-open-overlay');
  };

  /**
   * <overlay-dialog> bir <dialog> sarar. Tasarımdaki çekmeceler sayfa kabının (.aceso-site)
   * dışında açıldığı için element bağlanınca kendini <body> sonuna taşır.
   */
  class OverlayDialog extends HTMLElement {
    connectedCallback() {
      if (this.parentElement !== document.body) {
        const section = this.closest('.shopify-section');
        if (section) this.dataset.sectionId = section.id;
        document.body.append(this);
        return;
      }
      if (this.initialised) return;
      this.initialised = true;

      this.dialog = this.querySelector('dialog');
      if (!this.dialog) return;

      this.sideQuery = this.dataset.mobileSide ? window.matchMedia(this.dataset.mobileQuery || '(max-width: 800px)') : null;
      if (this.sideQuery) {
        this.desktopSide = this.dialog.dataset.side;
        this.onSideChange = () => {
          this.dialog.dataset.side = this.sideQuery.matches ? this.dataset.mobileSide : this.desktopSide;
        };
        this.sideQuery.addEventListener('change', this.onSideChange);
        this.onSideChange();
      }

      this.dialog.addEventListener('cancel', (event) => {
        event.preventDefault();
        this.close();
      });

      this.dialog.addEventListener('click', (event) => {
        if (event.target.closest('[data-overlay-close]')) {
          this.close();
          return;
        }
        if (event.target !== this.dialog) return;
        const rect = this.dialog.getBoundingClientRect();
        const outside =
          event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom;
        if (outside) this.close();
      });

      if (this.dataset.autoOpen === 'session') this.scheduleAutoOpen();
    }

    /* İlk ziyarette (oturum başına bir kez) kendiliğinden açılır: Sana Özel Seçki penceresi. */
    scheduleAutoOpen() {
      if (window.Shopify && window.Shopify.designMode) return;
      const key = `aceso-overlay-seen:${this.id}`;
      let seen = false;
      try {
        seen = window.sessionStorage.getItem(key) === '1';
        window.sessionStorage.setItem(key, '1');
      } catch (error) {
        /* Depolama kapalıysa ilk yüklemede göstermek yeterli. */
      }
      if (seen) return;
      this.autoOpenTimer = window.setTimeout(() => {
        if (openOverlayCount === 0) this.open(document.body);
      }, Number(this.dataset.delay || 1.2) * 1000);
    }

    disconnectedCallback() {
      if (this.parentElement === document.body) return;
      window.clearTimeout(this.autoOpenTimer);
      if (this.sideQuery) this.sideQuery.removeEventListener('change', this.onSideChange);
      if (this.isOpen) unlockScroll();
    }

    get isOpen() {
      return Boolean(this.dialog && this.dialog.open);
    }

    /** Açan öğe gerçek bir tetikleyici mi (kendiliğinden açılışta <body> olabilir)? */
    isTrigger(element) {
      return Boolean(element && element.matches && element.matches('[data-overlay-open], [aria-haspopup], [data-quick-add]'));
    }

    open(opener) {
      if (!this.dialog || this.isOpen) return;
      this.opener = opener || document.activeElement;
      window.clearTimeout(this.closeTimer);
      this.dialog.dataset.state = 'open';
      this.dialog.showModal();
      if (this.hasAttribute('data-focus-self')) {
        this.dialog.setAttribute('tabindex', '-1');
        this.dialog.focus({ preventScroll: true });
      }
      lockScroll();
      if (this.isTrigger(this.opener)) this.opener.setAttribute('aria-expanded', 'true');
      this.dispatchEvent(new CustomEvent('overlay:open', { bubbles: true }));
    }

    close() {
      if (!this.isOpen || this.dialog.dataset.state === 'closed') return;
      this.dialog.dataset.state = 'closed';
      const finish = () => {
        window.clearTimeout(this.closeTimer);
        if (!this.dialog.open) return;
        this.dialog.close();
        unlockScroll();
        if (this.isTrigger(this.opener)) this.opener.setAttribute('aria-expanded', 'false');
        if (this.opener && this.opener.isConnected && this.opener.focus) this.opener.focus({ preventScroll: true });
        this.dispatchEvent(new CustomEvent('overlay:close', { bubbles: true }));
      };
      if (theme.prefersReducedMotion()) {
        finish();
        return;
      }
      this.dialog.addEventListener('animationend', finish, { once: true });
      this.closeTimer = window.setTimeout(finish, 450);
    }
  }
  customElements.define('overlay-dialog', OverlayDialog);
  theme.OverlayDialog = OverlayDialog;

  /** Açma düğmeleri: data-overlay-open="ElementId" */
  document.addEventListener('click', (event) => {
    const trigger = event.target.closest('[data-overlay-open]');
    if (!trigger) return;
    const overlay = document.getElementById(trigger.dataset.overlayOpen);
    if (!overlay || typeof overlay.open !== 'function') return;
    event.preventDefault();
    overlay.open(trigger);
  });

  /* Tema düzenleyici: taşınmış çekmeceler bölümleriyle birlikte kaldırılır. */
  document.addEventListener('shopify:section:unload', (event) => {
    const sectionId = event.target.id;
    document.querySelectorAll(`body > [data-section-id="${CSS.escape(sectionId)}"]`).forEach((element) => {
      if (element.isOpen) unlockScroll();
      element.remove();
    });
  });

  document.addEventListener('shopify:section:select', (event) => {
    const overlay = document.querySelector(`body > [data-section-id="${CSS.escape(event.target.id)}"][data-open-in-editor]`);
    if (overlay) overlay.open();
  });

  document.addEventListener('shopify:section:deselect', (event) => {
    const overlay = document.querySelector(`body > [data-section-id="${CSS.escape(event.target.id)}"][data-open-in-editor]`);
    if (overlay) overlay.close();
  });

  /* ------------------------------------------------------------------ */
  /* Akordeon: açılış animasyonu için içerik yüksekliği                    */
  /* ------------------------------------------------------------------ */

  document.addEventListener(
    'toggle',
    (event) => {
      const item = event.target;
      if (!(item instanceof HTMLDetailsElement) || !item.matches('[data-slot="accordion-item"]') || !item.open) return;
      const content = item.querySelector('[data-slot="accordion-content"]');
      if (content) content.style.setProperty('--accordion-panel-height', `${content.scrollHeight}px`);
    },
    true,
  );

  /* ------------------------------------------------------------------ */
  /* Duyuru şeridi                                                        */
  /* ------------------------------------------------------------------ */

  class AnnouncementRotator extends HTMLElement {
    connectedCallback() {
      this.viewport = this.querySelector('.service-announcement__viewport');
      this.items = Array.from(this.viewport ? this.viewport.children : []);
      this.index = Math.max(0, this.items.findIndex((item) => !item.hidden));
      this.duration = 600;
      this.interval = Number(this.dataset.interval || 6) * 1000;
      if (this.items.length < 2) return;

      this.querySelector('[data-announcement-previous]')?.addEventListener('click', () => this.move('previous'));
      this.querySelector('[data-announcement-next]')?.addEventListener('click', () => this.move('next'));

      const pause = () => this.setPaused(true);
      const resume = () => this.setPaused(false);
      this.addEventListener('mouseenter', pause);
      this.addEventListener('mouseleave', resume);
      this.addEventListener('focusin', pause);
      this.addEventListener('focusout', resume);

      this.onBlockSelect = (event) => {
        const target = this.items.indexOf(event.target);
        if (target < 0) return;
        this.setPaused(true);
        this.show(target, 'next');
      };
      this.addEventListener('shopify:block:select', this.onBlockSelect);
      this.addEventListener('shopify:block:deselect', resume);

      this.start();
    }

    disconnectedCallback() {
      this.stop();
    }

    start() {
      this.stop();
      if (this.paused || theme.prefersReducedMotion() || this.dataset.autoplay === 'false') return;
      this.timer = window.setInterval(() => this.move('next'), this.interval);
    }

    stop() {
      window.clearInterval(this.timer);
    }

    setPaused(paused) {
      this.paused = paused;
      this.viewport.setAttribute('aria-live', paused ? 'polite' : 'off');
      if (paused) this.stop();
      else this.start();
    }

    move(direction) {
      const count = this.items.length;
      this.show((this.index + (direction === 'next' ? 1 : count - 1)) % count, direction);
    }

    show(nextIndex, direction) {
      if (nextIndex === this.index) return;
      const leaving = this.items[this.index];
      const entering = this.items[nextIndex];
      this.items.forEach((item) => {
        item.classList.remove('is-leaving', 'is-entering');
        if (item !== leaving && item !== entering) item.hidden = true;
      });
      this.viewport.classList.remove('is-next', 'is-previous');
      this.viewport.classList.add(`is-${direction}`);
      leaving.classList.add('is-leaving');
      leaving.setAttribute('aria-hidden', 'true');
      entering.hidden = false;
      entering.removeAttribute('aria-hidden');
      entering.classList.add('is-entering');
      this.index = nextIndex;
      window.clearTimeout(this.cleanup);
      this.cleanup = window.setTimeout(() => {
        leaving.hidden = true;
        leaving.classList.remove('is-leaving');
        entering.classList.remove('is-entering');
      }, this.duration);
    }
  }
  customElements.define('announcement-rotator', AnnouncementRotator);

  /* ------------------------------------------------------------------ */
  /* Header: kaydırma durumu, mega menü, mobil menü                       */
  /* ------------------------------------------------------------------ */

  class SiteHeader extends HTMLElement {
    connectedCallback() {
      this.onScroll = () => this.classList.toggle('is-scrolled', window.scrollY > 24);
      this.onScroll();
      window.addEventListener('scroll', this.onScroll, { passive: true });
      this.setupMegaMenu();
      this.setupCollapse();
    }

    disconnectedCallback() {
      window.removeEventListener('scroll', this.onScroll);
      this.resizeObserver?.disconnect();
    }

    /* Menü öğeleri logonun solundaki alana sığmıyorsa (uzun ya da çok sayıda öğe) önce
       öğe aralıkları daraltılır; yine sığmıyorsa masaüstünde de çekmece menüye geçilir.
       Öğeler hiçbir durumda logonun üstüne binmez. */
    setupCollapse() {
      const nav = this.querySelector('.desktop-nav');
      const inner = this.querySelector('.header-inner');
      if (!nav || !inner || !this.navigation || !('ResizeObserver' in window)) return;
      const logo = this.querySelector('.header-inner > .wordmark');
      const check = () => {
        this.classList.remove('is-nav-collapsed', 'is-nav-tight');
        /* Dar ekranda menü zaten gizlidir; ölçülecek bir şey yok. */
        if (!logo || nav.offsetParent === null) return;
        const available = logo.getBoundingClientRect().left - nav.getBoundingClientRect().left - 16;
        if (nav.scrollWidth <= available) return;
        this.classList.add('is-nav-tight');
        if (nav.scrollWidth <= available) return;
        this.classList.remove('is-nav-tight');
        this.classList.add('is-nav-collapsed');
      };
      this.resizeObserver = new ResizeObserver(() => window.requestAnimationFrame(check));
      this.resizeObserver.observe(inner);
      if (document.fonts && document.fonts.ready) document.fonts.ready.then(check);
      check();
    }

    setupMegaMenu() {
      this.navigation = this.querySelector('.mega-navigation');
      this.menu = this.querySelector('.mega-menu');
      if (!this.navigation || !this.menu) return;
      this.links = Array.from(this.navigation.querySelectorAll('.desktop-nav > a[data-nav-index]'));
      this.panels = Array.from(this.menu.querySelectorAll('[data-nav-panel]'));
      const canHover = window.matchMedia('(hover: hover)');

      this.links.forEach((link) => {
        link.addEventListener('mouseenter', () => this.activate(link));
        link.addEventListener('focus', () => this.activate(link));
        link.addEventListener('click', (event) => {
          /* Dokunmatik geniş ekranlarda ilk dokunuş menüyü açar, ikincisi bağlantıya gider. */
          if (canHover.matches || link.getAttribute('aria-expanded') === 'true') return;
          if (!this.panelFor(link)) return;
          event.preventDefault();
          this.activate(link);
        });
        link.addEventListener('keydown', (event) => {
          if (event.key !== 'ArrowDown') return;
          const panel = this.panelFor(link);
          const first = panel && panel.querySelector('a');
          if (!first) return;
          event.preventDefault();
          first.focus();
        });
      });

      this.navigation.addEventListener('mouseleave', () => this.deactivate());
      this.navigation.addEventListener('focusout', (event) => {
        if (!this.navigation.contains(event.relatedTarget)) this.deactivate();
      });
      this.navigation.addEventListener('keydown', (event) => {
        if (event.key !== 'Escape' || !this.active) return;
        const link = this.active;
        this.deactivate();
        link.focus();
      });
    }

    panelFor(link) {
      return this.panels.find((panel) => panel.dataset.navPanel === link.dataset.navIndex);
    }

    activate(link) {
      const panel = this.panelFor(link);
      this.active = link;
      this.links.forEach((item) => item.setAttribute('aria-expanded', String(item === link && Boolean(panel))));
      this.panels.forEach((item) => {
        item.hidden = item !== panel;
      });
      this.menu.classList.toggle('is-open', Boolean(panel));
      this.menu.setAttribute('aria-hidden', String(!panel));
    }

    deactivate() {
      if (!this.menu) return;
      this.active = null;
      this.links.forEach((item) => item.setAttribute('aria-expanded', 'false'));
      this.menu.classList.remove('is-open');
      this.menu.setAttribute('aria-hidden', 'true');
    }
  }
  customElements.define('site-header', SiteHeader);

  /** Mobil menüde kategoriye girince açılan alt menü. */
  class MobileMenu extends HTMLElement {
    connectedCallback() {
      this.addEventListener('click', (event) => {
        const opener = event.target.closest('[data-drill-open]');
        if (opener) {
          this.show(opener.dataset.drillOpen);
          return;
        }
        if (event.target.closest('[data-drill-close]')) this.show(null);
      });
      const overlay = this.closest('overlay-dialog');
      if (overlay) overlay.addEventListener('overlay:close', () => this.show(null));
    }

    show(id) {
      this.querySelectorAll('[data-drill]').forEach((panel) => {
        panel.hidden = panel.dataset.drill !== id;
      });
      const active = id && this.querySelector(`[data-drill="${CSS.escape(id)}"]`);
      if (active) active.querySelector('button, a')?.focus({ preventScroll: true });
    }
  }
  customElements.define('mobile-menu', MobileMenu);

  /* ------------------------------------------------------------------ */
  /* Arama: yazılan placeholder ve canlı öneriler                          */
  /* ------------------------------------------------------------------ */

  class PredictiveSearch extends HTMLElement {
    connectedCallback() {
      this.input = this.querySelector('input[name="q"]');
      this.results = this.querySelector('[data-predictive-results]');
      this.idle = this.querySelector('[data-predictive-idle]');
      this.variant = this.dataset.variant || 'sheet';
      if (!this.input) return;

      this.input.addEventListener('input', theme.debounce(() => this.update(), 220));
      this.input.addEventListener('focus', () => {
        this.focused = true;
        if (this.input.value.trim().length >= 2) this.setOpen(true);
        this.stopTyping();
      });
      this.input.addEventListener('blur', () => {
        this.focused = false;
        this.startTyping();
      });
      this.addEventListener('focusout', (event) => {
        if (this.variant === 'inline' && !this.contains(event.relatedTarget)) this.setOpen(false);
      });
      this.addEventListener('keydown', (event) => this.onKeydown(event));
      this.startTyping();
    }

    disconnectedCallback() {
      this.stopTyping();
      if (this.controller) this.controller.abort();
    }

    /* Placeholder'da kelimeyi harf harf yazar, bekler, siler ve sıradakine geçer. */
    startTyping() {
      const words = (this.dataset.typingWords || '').split('|').filter(Boolean);
      if (!words.length || this.input.value !== '' || this.focused) return;
      const prefix = this.dataset.typingPrefix || '';
      if (theme.prefersReducedMotion()) {
        this.input.placeholder = prefix + words[0];
        return;
      }
      let wordIndex = 0;
      let length = 0;
      let deleting = false;
      const tick = () => {
        const word = words[wordIndex];
        length += deleting ? -1 : 1;
        this.input.placeholder = prefix + word.slice(0, length);
        let delay = deleting ? 45 : 110;
        if (!deleting && length === word.length) {
          deleting = true;
          delay = 1600;
        } else if (deleting && length === 0) {
          deleting = false;
          wordIndex = (wordIndex + 1) % words.length;
          delay = 350;
        }
        this.typingTimer = window.setTimeout(tick, delay);
      };
      this.stopTyping(false);
      this.input.placeholder = prefix;
      this.typingTimer = window.setTimeout(tick, 400);
    }

    stopTyping(restore = true) {
      window.clearTimeout(this.typingTimer);
      if (restore && this.dataset.focusPlaceholder) this.input.placeholder = this.dataset.focusPlaceholder;
    }

    setOpen(open) {
      if (!this.results) return;
      this.results.hidden = !open;
      if (this.idle) this.idle.hidden = open;
      this.input.setAttribute('aria-expanded', String(open));
    }

    async update() {
      const query = this.input.value.trim();
      if (!this.results || this.dataset.predictive === 'false') return;
      if (query.length < 2) {
        this.setOpen(false);
        this.results.innerHTML = '';
        return;
      }
      if (this.controller) this.controller.abort();
      this.controller = new AbortController();
      const params = new URLSearchParams({
        q: query,
        'resources[type]': 'product,collection',
        'resources[limit]': this.variant === 'inline' ? '5' : '6',
        'resources[options][fields]': 'title,product_type,variants.title,variants.sku,vendor,tag',
        section_id: 'predictive-search',
      });
      try {
        const doc = await theme.fetchDocument(`${theme.routes.predictiveSearch}?${params}`, {
          signal: this.controller.signal,
        });
        const markup = doc.querySelector(`[data-predictive-variant="${this.variant}"]`);
        if (!markup) return;
        this.results.innerHTML = markup.innerHTML;
        this.activeIndex = -1;
        this.setOpen(true);
      } catch (error) {
        if (error.name !== 'AbortError') this.setOpen(false);
      }
    }

    onKeydown(event) {
      if (event.key === 'Escape' && this.variant === 'inline') {
        this.setOpen(false);
        return;
      }
      const options = Array.from(this.querySelectorAll('[data-predictive-option]'));
      if (!options.length || !['ArrowDown', 'ArrowUp'].includes(event.key)) return;
      event.preventDefault();
      const current = options.indexOf(document.activeElement);
      const next =
        event.key === 'ArrowDown' ? (current + 1) % options.length : current <= 0 ? options.length - 1 : current - 1;
      options[next].focus();
    }
  }
  customElements.define('predictive-search', PredictiveSearch);

  /* ------------------------------------------------------------------ */
  /* Kampanya (hero) slaytları                                            */
  /* ------------------------------------------------------------------ */

  class CampaignCarousel extends HTMLElement {
    connectedCallback() {
      this.slides = Array.from(this.querySelectorAll('.fresh-campaign-carousel__slide'));
      this.dots = Array.from(this.querySelectorAll('.fresh-campaign-carousel__pagination-button'));
      this.viewport = this.querySelector('.fresh-campaign-carousel__viewport');
      this.index = Math.max(0, this.slides.findIndex((slide) => !slide.hidden));
      this.delay = Number(this.dataset.delay || 6) * 1000;
      this.pauseReasons = new Set();
      this.setupScreenHeight();
      this.setupVideos();
      if (this.slides.length < 2) return;

      this.querySelector('[data-carousel-previous]')?.addEventListener('click', () => this.goTo(this.index - 1));
      this.querySelector('[data-carousel-next]')?.addEventListener('click', () => this.goTo(this.index + 1));
      this.dots.forEach((dot, index) => dot.addEventListener('click', () => this.goTo(index)));

      this.addEventListener('mouseenter', () => this.pause('hover'));
      this.addEventListener('mouseleave', () => this.resume('hover'));
      this.addEventListener('focusin', () => this.pause('focus'));
      this.addEventListener('focusout', (event) => {
        if (!this.contains(event.relatedTarget)) this.resume('focus');
      });

      this.addEventListener(
        'touchstart',
        (event) => {
          const touch = event.touches[0];
          if (!touch) return;
          this.swipeOrigin = { x: touch.clientX, y: touch.clientY };
          this.pause('touch');
        },
        { passive: true },
      );
      const finishTouch = (event) => {
        const origin = this.swipeOrigin;
        const touch = event && event.changedTouches && event.changedTouches[0];
        this.swipeOrigin = null;
        this.resume('touch');
        if (!origin || !touch) return;
        const deltaX = touch.clientX - origin.x;
        const deltaY = touch.clientY - origin.y;
        if (Math.abs(deltaX) < 44 || Math.abs(deltaX) <= Math.abs(deltaY)) return;
        this.goTo(this.index + (deltaX < 0 ? 1 : -1));
      };
      this.addEventListener('touchend', finishTouch);
      this.addEventListener('touchcancel', () => finishTouch());

      this.addEventListener('shopify:block:select', (event) => {
        const target = this.slides.indexOf(event.target);
        if (target < 0) return;
        this.pause('editor');
        this.goTo(target);
      });
      this.addEventListener('shopify:block:deselect', () => this.resume('editor'));

      this.schedule();
    }

    disconnectedCallback() {
      window.clearTimeout(this.timer);
      this.offsetObserver?.disconnect();
      if (this.measureOffset) window.removeEventListener('resize', this.measureOffset);
      if (this.videoQuery) this.videoQuery.removeEventListener('change', this.onVideoQuery);
    }

    /* "Ekrana sığdır": bölümün üstünde kalan yükseklik (duyuru şeridi vb.) ölçülür; CSS
       yüksekliği 100svh − bu değer olarak hesaplar. Bölüm sayfanın aşağısındaysa
       çıkarılacak bir şey yoktur, tam ekran yüksekliği kullanılır. */
    setupScreenHeight() {
      if (!this.classList.contains('fresh-hero-campaign--screen')) return;
      this.measureOffset = () => {
        const top = this.getBoundingClientRect().top + window.scrollY;
        this.style.setProperty('--hero-offset', `${top < window.innerHeight / 2 ? Math.round(top) : 0}px`);
      };
      this.measureOffset();
      window.addEventListener('resize', this.measureOffset);
      if ('ResizeObserver' in window) {
        this.offsetObserver = new ResizeObserver(() => this.measureOffset());
        document
          .querySelectorAll('.shopify-section-group-header-group')
          .forEach((section) => this.offsetObserver.observe(section));
      }
    }

    /* Slayt videoları kendiliğinden yüklenmez: yalnızca etkin slaytın, o ekran genişliğinde
       görünen videosu oynatılır. Hareket azaltma tercihinde video duraklatılmış başlar. */
    setupVideos() {
      if (!this.querySelector('video')) return;
      this.videoToggle = this.querySelector('[data-video-toggle]');
      this.videoPaused = theme.prefersReducedMotion();
      this.videoToggle?.addEventListener('click', () => {
        this.videoPaused = !this.videoPaused;
        this.syncVideos();
      });
      this.videoQuery = window.matchMedia('(max-width: 1023px)');
      this.onVideoQuery = () => this.syncVideos();
      this.videoQuery.addEventListener('change', this.onVideoQuery);
      this.syncVideos();
    }

    syncVideos() {
      if (!this.videoQuery) return;
      let activeHasVideo = false;
      this.slides.forEach((slide, index) => {
        slide.querySelectorAll('video').forEach((video) => {
          /* offsetParent, gizli slaytta ve o genişlikte gösterilmeyen videoda null olur. */
          const visible = index === this.index && video.offsetParent !== null;
          if (!visible || this.videoPaused) {
            video.pause();
          } else {
            const playing = video.play();
            if (playing && playing.catch) playing.catch(() => {});
          }
          if (visible) activeHasVideo = true;
        });
      });
      const toggle = this.videoToggle;
      if (!toggle) return;
      toggle.hidden = !activeHasVideo;
      toggle.setAttribute('aria-label', this.videoPaused ? toggle.dataset.labelPlay : toggle.dataset.labelPause);
      const pauseIcon = toggle.querySelector('[data-icon-pause]');
      const playIcon = toggle.querySelector('[data-icon-play]');
      if (pauseIcon) pauseIcon.hidden = this.videoPaused;
      if (playIcon) playIcon.hidden = !this.videoPaused;
    }

    pause(reason) {
      this.pauseReasons.add(reason);
      this.viewport?.setAttribute('aria-live', 'polite');
      window.clearTimeout(this.timer);
    }

    resume(reason) {
      this.pauseReasons.delete(reason);
      if (this.pauseReasons.size > 0) return;
      this.viewport?.setAttribute('aria-live', 'off');
      this.schedule();
    }

    schedule() {
      window.clearTimeout(this.timer);
      if (this.dataset.autoplay === 'false' || theme.prefersReducedMotion() || this.pauseReasons.size > 0) return;
      this.timer = window.setTimeout(() => this.goTo(this.index + 1), this.delay);
    }

    goTo(nextIndex) {
      const count = this.slides.length;
      this.index = ((nextIndex % count) + count) % count;
      this.slides.forEach((slide, index) => {
        slide.hidden = index !== this.index;
      });
      this.dots.forEach((dot, index) => {
        const active = index === this.index;
        dot.classList.toggle('is-active', active);
        dot.dataset.active = String(active);
        if (active) dot.setAttribute('aria-current', 'true');
        else dot.removeAttribute('aria-current');
      });
      this.syncVideos();
      this.schedule();
    }
  }
  customElements.define('campaign-carousel', CampaignCarousel);

  /* ------------------------------------------------------------------ */
  /* Yatay raylar                                                         */
  /* ------------------------------------------------------------------ */

  /** Oklarla kaydırılan basit ray (ACESO Günlük). */
  class ScrollRail extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('[data-rail-track]');
      if (!this.track) return;
      const scroll = (direction) =>
        this.track.scrollBy({
          left: direction * this.track.clientWidth * 0.75,
          behavior: theme.prefersReducedMotion() ? 'auto' : 'smooth',
        });
      this.querySelector('[data-rail-previous]')?.addEventListener('click', () => scroll(-1));
      this.querySelector('[data-rail-next]')?.addEventListener('click', () => scroll(1));
    }
  }
  customElements.define('scroll-rail', ScrollRail);

  /** #acesostili: görsel üzerindeki noktalar küçük ürün kartı açar. */
  class LookRail extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('.look-rail__track');
      this.previous = this.querySelector('.look-rail__arrow.is-previous');
      this.next = this.querySelector('.look-rail__arrow.is-next');
      if (!this.track) return;

      this.onScroll = () => this.updateEdges();
      this.track.addEventListener('scroll', this.onScroll, { passive: true });
      this.previous?.addEventListener('click', () => this.scrollByPage(-1));
      this.next?.addEventListener('click', () => this.scrollByPage(1));
      this.updateEdges();

      this.addEventListener('click', (event) => {
        const hotspot = event.target.closest('.look-hotspot');
        if (!hotspot) return;
        const wasOpen = hotspot.classList.contains('is-open');
        this.closeAll();
        if (!wasOpen) this.toggle(hotspot, true);
      });

      this.onOutside = (event) => {
        if (!event.target.closest || !event.target.closest('.look-hotspot, .look-card')) this.closeAll();
      };
      this.onKey = (event) => {
        if (event.key === 'Escape') this.closeAll();
      };
      document.addEventListener('mousedown', this.onOutside);
      document.addEventListener('keydown', this.onKey);

      this.addEventListener('shopify:block:select', (event) => {
        event.target.scrollIntoView({ block: 'nearest', inline: 'start', behavior: 'smooth' });
      });
    }

    disconnectedCallback() {
      document.removeEventListener('mousedown', this.onOutside);
      document.removeEventListener('keydown', this.onKey);
    }

    toggle(hotspot, open) {
      hotspot.classList.toggle('is-open', open);
      hotspot.setAttribute('aria-expanded', String(open));
      const card = hotspot.parentElement.querySelector('.look-card');
      if (card) card.hidden = !open;
    }

    closeAll() {
      this.querySelectorAll('.look-hotspot.is-open').forEach((hotspot) => this.toggle(hotspot, false));
    }

    updateEdges() {
      const { scrollLeft, clientWidth, scrollWidth } = this.track;
      if (this.previous) this.previous.disabled = scrollLeft <= 4;
      if (this.next) this.next.disabled = scrollLeft + clientWidth >= scrollWidth - 4;
    }

    scrollByPage(direction) {
      const item = this.track.querySelector('.look-rail__item');
      if (!item) return;
      const gap = parseFloat(window.getComputedStyle(this.track).columnGap || '0');
      const step = item.offsetWidth + gap;
      this.closeAll();
      this.track.scrollBy({
        left: direction * step * (window.innerWidth >= 1024 ? 4 : 1),
        behavior: theme.prefersReducedMotion() ? 'auto' : 'smooth',
      });
    }
  }
  customElements.define('look-rail', LookRail);

  /* ------------------------------------------------------------------ */
  /* Favoriler                                                            */
  /* ------------------------------------------------------------------ */

  const FAVORITES_KEY = 'aceso:favorites';

  const favorites = {
    all() {
      const list = storage.read(FAVORITES_KEY, []);
      return Array.isArray(list) ? list.filter((item) => typeof item === 'string') : [];
    },
    has(handle) {
      return this.all().includes(handle);
    },
    toggle(handle) {
      const list = this.all();
      const index = list.indexOf(handle);
      if (index > -1) list.splice(index, 1);
      else list.push(handle);
      storage.write(FAVORITES_KEY, list);
      this.sync();
      document.dispatchEvent(new CustomEvent('favorites:change', { detail: { handle, liked: index === -1, list } }));
      return index === -1;
    },
    /** Sayfadaki tüm kalp düğmelerini ve header sayacını güncel duruma getirir. */
    sync(scope = document) {
      const list = this.all();
      scope.querySelectorAll('[data-favorite-toggle]').forEach((button) => {
        const liked = list.includes(button.dataset.favoriteToggle);
        button.classList.toggle('is-liked', liked);
        button.setAttribute('aria-pressed', String(liked));
        button.setAttribute('aria-label', liked ? theme.strings.removeFromFavorites : theme.strings.addToFavorites);
        const icon = button.querySelector('svg');
        if (icon) icon.setAttribute('fill', liked ? 'currentColor' : 'none');
      });
      document.querySelectorAll('[data-favorites-count]').forEach((badge) => {
        badge.textContent = String(list.length);
        badge.hidden = list.length === 0;
      });
      document.querySelectorAll('[data-favorites-label]').forEach((link) => {
        link.setAttribute('aria-label', theme.strings.favoritesCount.replace('[count]', String(list.length)));
      });
    },
  };
  theme.favorites = favorites;

  document.addEventListener('click', (event) => {
    const button = event.target.closest('[data-favorite-toggle]');
    if (!button) return;
    event.preventDefault();
    favorites.toggle(button.dataset.favoriteToggle);
  });

  window.addEventListener('storage', (event) => {
    if (event.key === FAVORITES_KEY) favorites.sync();
  });

  /** Favoriler sayfasındaki ürün ızgarası. */
  class FavoritesGrid extends HTMLElement {
    connectedCallback() {
      this.grid = this.querySelector('[data-favorites-grid]');
      this.filled = this.querySelector('[data-favorites-filled]');
      this.emptyTemplate = this.querySelector('[data-favorites-empty-template]');
      this.count = this.querySelector('[data-favorites-total]');
      this.onChange = (event) => {
        if (event.detail.liked) return;
        this.grid.querySelector(`product-card[data-product-handle="${CSS.escape(event.detail.handle)}"]`)?.remove();
        this.updateState(this.grid.querySelectorAll('product-card').length);
      };
      document.addEventListener('favorites:change', this.onChange);
      this.load();
    }

    disconnectedCallback() {
      document.removeEventListener('favorites:change', this.onChange);
    }

    async load() {
      const handles = favorites.all();
      this.updateState(handles.length);
      if (!handles.length) return;
      this.setAttribute('aria-busy', 'true');
      const cards = await Promise.all(handles.map((handle) => theme.fetchProductCard(handle)));
      const found = cards.filter(Boolean);
      this.grid.replaceChildren(...found);
      this.removeAttribute('aria-busy');
      this.updateState(found.length);
      favorites.sync(this);
    }

    updateState(total) {
      if (this.filled) this.filled.hidden = total === 0;
      const empty = this.querySelector('.favorites-empty');
      if (total === 0 && !empty && this.emptyTemplate) {
        this.filled.after(this.emptyTemplate.content.firstElementChild.cloneNode(true));
      } else if (total > 0 && empty) {
        empty.remove();
      }
      if (this.count) this.count.textContent = this.count.dataset.template.replace('[count]', String(total));
    }
  }
  customElements.define('favorites-grid', FavoritesGrid);

  /* ------------------------------------------------------------------ */
  /* Ürün kartı: hover galerisi                                           */
  /* ------------------------------------------------------------------ */

  const finePointer = window.matchMedia('(hover: hover) and (pointer: fine)');

  class ProductCard extends HTMLElement {
    connectedCallback() {
      if (this.ready) return;
      this.ready = true;
      this.media = this.querySelector('.product-media');
      this.link = this.media && this.media.querySelector('a[data-hover-images]');
      favorites.sync(this);
      if (!this.link) return;
      this.sources = this.link.dataset.hoverImages.split('|').filter(Boolean);
      this.indicators = Array.from(this.link.querySelectorAll('.product-hover-indicators > i'));

      this.link.addEventListener('mouseenter', () => {
        if (!finePointer.matches) return;
        this.ensureImages();
        this.media.classList.add('is-hover-active');
      });
      this.link.addEventListener('mouseleave', () => {
        this.media.classList.remove('is-hover-active');
        this.setIndex(0);
      });
      this.link.addEventListener('mousemove', (event) => {
        if (!finePointer.matches) return;
        const rect = this.link.getBoundingClientRect();
        const total = this.sources.length + 1;
        const index = Math.min(total - 1, Math.max(0, Math.floor(((event.clientX - rect.left) / rect.width) * total)));
        this.setIndex(index);
      });
    }

    ensureImages() {
      if (this.hoverImages) return;
      this.hoverImages = this.sources.map((source) => {
        const image = document.createElement('img');
        image.className = 'product-image-hover';
        image.alt = '';
        image.width = 800;
        image.height = 1155;
        image.src = source;
        return image;
      });
      this.link.querySelector('.product-image-primary')?.after(...this.hoverImages);
    }

    setIndex(index) {
      if (this.hoverIndex === index) return;
      this.hoverIndex = index;
      (this.hoverImages || []).forEach((image, position) => image.classList.toggle('is-active', position + 1 === index));
      this.indicators.forEach((indicator, position) => indicator.classList.toggle('is-active', position === index));
    }
  }
  customElements.define('product-card', ProductCard);

  /* ------------------------------------------------------------------ */
  /* Sepet                                                                */
  /* ------------------------------------------------------------------ */

  const cart = {
    /** Sepet değişince yeniden çizilecek bölümlerin kimlikleri. */
    sectionIds() {
      return Array.from(document.querySelectorAll('[data-cart-section]'))
        .map((element) => element.dataset.cartSection)
        .filter((id, index, list) => id && list.indexOf(id) === index);
    },

    async request(url, body) {
      const response = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ ...body, sections: this.sectionIds().join(','), sections_url: window.location.pathname }),
      });
      const data = await response.json();
      if (!response.ok || data.status) {
        const error = new Error(data.description || data.message || theme.strings.cartError);
        error.response = data;
        throw error;
      }
      return data;
    },

    render(sections) {
      if (!sections) return;
      let count = null;
      Object.entries(sections).forEach(([id, html]) => {
        if (!html) return;
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const source = doc.querySelector(`[data-cart-section="${CSS.escape(id)}"]`);
        if (!source) return;
        document.querySelectorAll(`[data-cart-section="${CSS.escape(id)}"]`).forEach((target) => {
          target.className = source.className;
          target.innerHTML = source.innerHTML;
        });
        if (source.dataset.cartCount != null) count = Number(source.dataset.cartCount);
      });
      if (count != null) this.setCount(count);
      document.dispatchEvent(new CustomEvent('cart:updated', { detail: { count } }));
    },

    setCount(count) {
      document.querySelectorAll('[data-cart-count-bubble]').forEach((badge) => {
        badge.textContent = String(count);
        badge.hidden = count === 0;
      });
      document.querySelectorAll('[data-cart-label]').forEach((button) => {
        button.setAttribute('aria-label', button.dataset.cartLabel.replace('[count]', String(count)));
      });
    },

    async add(items) {
      const data = await this.request(theme.routes.cartAdd, { items });
      this.render(data.sections);
      return data;
    },

    async change(line, quantity) {
      const data = await this.request(theme.routes.cartChange, { line, quantity });
      this.render(data.sections);
      return data;
    },

    async updateNote(note) {
      return this.request(theme.routes.cartUpdate, { note });
    },

    /** Ürün eklendikten sonra sepeti gösterir: çekmece açıksa çekmeceyi, değilse sepet sayfasını. */
    reveal(opener) {
      const drawer = document.getElementById('CartDrawer');
      if (theme.settings.cartType === 'drawer' && drawer && typeof drawer.open === 'function') drawer.open(opener);
      else window.location.assign(theme.routes.cart);
    },
  };
  theme.cart = cart;

  /** Sepet satırları: adet düğmeleri, silme ve not. Çekmecede de sepet sayfasında da kullanılır. */
  class CartItems extends HTMLElement {
    connectedCallback() {
      this.addEventListener('click', (event) => {
        const button = event.target.closest('[data-cart-quantity]');
        if (!button) return;
        event.preventDefault();
        this.change(Number(button.dataset.line), Number(button.dataset.cartQuantity), button);
      });
      this.addEventListener('change', (event) => {
        const input = event.target.closest('[data-cart-quantity-input]');
        if (input) {
          this.change(Number(input.dataset.line), Math.max(0, Number(input.value) || 0), input);
          return;
        }
        const note = event.target.closest('[data-cart-note]');
        if (note) cart.updateNote(note.value).catch(() => {});
      });
    }

    async change(line, quantity, control) {
      this.setAttribute('aria-busy', 'true');
      const focusKey = control && control.dataset.focusKey;
      try {
        await cart.change(line, quantity);
        this.showError('');
      } catch (error) {
        this.showError(error.message);
      } finally {
        this.removeAttribute('aria-busy');
        if (focusKey) document.querySelector(`[data-focus-key="${CSS.escape(focusKey)}"]`)?.focus();
      }
    }

    showError(message) {
      const target = this.querySelector('[data-cart-error]');
      if (!target) return;
      target.textContent = message;
      target.hidden = !message;
    }
  }
  customElements.define('cart-items', CartItems);

  /* ------------------------------------------------------------------ */
  /* Varyant seçimi ve sepete ekleme (ürün sayfası + hızlı ekle)           */
  /* ------------------------------------------------------------------ */

  /**
   * Seçenek düğmelerinden varyantı çözer. Tasarım gereği hiçbir beden önceden seçili
   * gelmez; kullanıcı seçmeden sepete eklemeye çalışırsa uyarı gösterilir.
   */
  class VariantPicker extends HTMLElement {
    connectedCallback() {
      const data = this.querySelector('script[type="application/json"]');
      this.variants = data ? JSON.parse(data.textContent) : [];
      this.groups = Array.from(this.querySelectorAll('[data-option-group]'));
      this.selected = this.groups.map((group) => {
        const active = group.querySelector('button.selected');
        return active ? active.dataset.value : null;
      });

      this.addEventListener('click', (event) => {
        const button = event.target.closest('[data-option-group] button[data-value]');
        if (!button || button.disabled) return;
        const position = this.groups.indexOf(button.closest('[data-option-group]'));
        this.select(position, button.dataset.value);
      });

      this.refresh();
    }

    get variant() {
      if (!this.groups.length) return this.variants[0] || null;
      if (this.selected.some((value) => value == null)) return null;
      return this.variants.find((variant) => variant.options.every((value, index) => value === this.selected[index])) || null;
    }

    get complete() {
      return this.selected.every((value) => value != null);
    }

    select(position, value) {
      this.selected[position] = value;
      this.refresh();
      this.dispatchEvent(
        new CustomEvent('variant:change', {
          bubbles: true,
          detail: { variant: this.variant, selected: [...this.selected], complete: this.complete },
        }),
      );
    }

    /** Diğer seçimlerle birlikte satın alınamayan değerleri "tükendi" olarak işaretler. */
    refresh() {
      this.groups.forEach((group, position) => {
        group.querySelectorAll('button[data-value]').forEach((button) => {
          const value = button.dataset.value;
          const available = this.variants.some(
            (variant) =>
              variant.available &&
              variant.options[position] === value &&
              variant.options.every((option, index) => index === position || this.selected[index] == null || this.selected[index] === option),
          );
          const selected = this.selected[position] === value;
          button.classList.toggle('selected', selected);
          button.setAttribute('aria-pressed', String(selected));
          button.dataset.available = String(available);
          if (this.dataset.disableUnavailable === 'true') button.disabled = !available;
          const note = button.querySelector('small');
          if (note) note.hidden = available;
        });
        const label = this.querySelector(`[data-option-value="${position}"]`);
        if (label) {
          label.textContent = this.selected[position] || '';
          const wrapper = label.closest('[data-option-value-wrapper]');
          if (wrapper) wrapper.hidden = !this.selected[position];
        }
      });
    }

    /** Seçilmemiş ilk seçenek grubunu işaretler ve odağı oraya taşır. */
    showError(show) {
      const error = this.querySelector('[data-option-error]');
      if (error) error.hidden = !show;
      let focused = false;
      this.groups.forEach((group, position) => {
        const missing = show && this.selected[position] == null;
        group.classList.toggle('has-error', missing);
        if (missing && !focused) {
          group.focus();
          focused = true;
        }
      });
    }
  }
  customElements.define('variant-picker', VariantPicker);
  theme.VariantPicker = VariantPicker;

  /* ------------------------------------------------------------------ */
  /* Hızlı ekle çekmecesi                                                 */
  /* ------------------------------------------------------------------ */

  class QuickAdd extends HTMLElement {
    connectedCallback() {
      this.addEventListener('variant:change', (event) => this.onVariantChange(event.detail));
      this.addEventListener('click', (event) => {
        if (event.target.closest('[data-quick-submit]')) this.submit(event.target.closest('[data-quick-submit]'));
      });
    }

    onVariantChange({ variant, selected, complete }) {
      const button = this.querySelector('[data-quick-submit]');
      const price = this.querySelector('[data-quick-price]');
      if (!button) return;
      if (variant && price) price.textContent = variant.price_formatted;
      if (!complete) {
        button.disabled = true;
        button.textContent = button.dataset.labelIdle;
        return;
      }
      if (!variant || !variant.available) {
        button.disabled = true;
        button.textContent = button.dataset.labelSoldOut;
        return;
      }
      button.disabled = false;
      const size = selected[selected.length - 1];
      /* Değerin içinde zaten "beden" geçiyorsa ("BEDEN 1") sözcük yinelenmez. */
      const template = theme.mentionsSize(size) ? button.dataset.labelReadyGeneric : button.dataset.labelReady;
      button.textContent = template.replace('[size]', size);
    }

    async submit(button) {
      const picker = this.querySelector('variant-picker');
      const variant = picker ? picker.variant : null;
      if (!variant) return;
      button.disabled = true;
      button.setAttribute('aria-busy', 'true');
      const error = this.querySelector('[data-quick-error]');
      try {
        await cart.add([{ id: variant.id, quantity: 1 }]);
        const overlay = this.closest('overlay-dialog');
        const opener = overlay && overlay.opener;
        if (overlay) {
          overlay.addEventListener('overlay:close', () => cart.reveal(opener), { once: true });
          overlay.close();
        } else {
          cart.reveal();
        }
      } catch (failure) {
        if (error) {
          error.textContent = failure.message;
          error.hidden = false;
        }
        button.disabled = false;
      } finally {
        button.removeAttribute('aria-busy');
      }
    }
  }
  customElements.define('quick-add', QuickAdd);

  /**
   * Karttaki beden kutusu. Tek seçenekli üründe "+" düğmesi kartın altında bu kutuya
   * açılır; aynı anda yalnızca bir kartın kutusu açık olur.
   */
  const quickSizes = {
    current: null,

    open(trigger, panel) {
      this.close();
      this.current = { trigger, panel };
      panel.hidden = false;
      /* Geçişin çalışması için kutu önce kapalı hâliyle çizilmelidir. */
      panel.getBoundingClientRect();
      panel.classList.add('is-open');
      trigger.setAttribute('aria-expanded', 'true');
      trigger.closest('.product-media')?.classList.add('has-open-sizes');
      const first = panel.querySelector('.card-sizes__options button:not(:disabled)');
      (first || panel.querySelector('[data-card-sizes-close]'))?.focus({ preventScroll: true });
    },

    close(restoreFocus) {
      const open = this.current;
      if (!open) return;
      this.current = null;
      const { trigger, panel } = open;
      panel.classList.remove('is-open');
      trigger.setAttribute('aria-expanded', 'false');
      trigger.closest('.product-media')?.classList.remove('has-open-sizes');
      this.showError(panel, '');
      const hide = () => {
        if (!panel.classList.contains('is-open')) panel.hidden = true;
      };
      if (theme.prefersReducedMotion()) hide();
      else window.setTimeout(hide, 240);
      if (restoreFocus) trigger.focus({ preventScroll: true });
    },

    showError(panel, message) {
      const error = panel.querySelector('[data-card-sizes-error]');
      if (!error) return;
      error.textContent = message;
      error.hidden = !message;
    },
  };

  /** Karttan bir varyantı sepete ekler ve sepeti gösterir. */
  async function quickAddVariant(control, opener, panel) {
    if (control.getAttribute('aria-busy') === 'true') return;
    control.setAttribute('aria-busy', 'true');
    try {
      await cart.add([{ id: Number(control.dataset.variantId), quantity: 1 }]);
      quickSizes.close();
      cart.reveal(opener);
    } catch (failure) {
      if (panel) quickSizes.showError(panel, failure.message);
      else window.location.assign(opener.dataset.quickAdd);
    } finally {
      control.removeAttribute('aria-busy');
    }
  }

  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape' && quickSizes.current) quickSizes.close(true);
  });

  document.addEventListener('click', async (event) => {
    const sizeButton = event.target.closest('[data-card-sizes] [data-variant-id]');
    if (sizeButton) {
      event.preventDefault();
      const panel = sizeButton.closest('[data-card-sizes]');
      const opener = quickSizes.current && quickSizes.current.panel === panel ? quickSizes.current.trigger : null;
      if (!sizeButton.disabled) quickAddVariant(sizeButton, opener, panel);
      return;
    }
    if (event.target.closest('[data-card-sizes-close]')) {
      quickSizes.close(true);
      return;
    }

    const trigger = event.target.closest('[data-quick-add]');
    if (!trigger) {
      /* Kutunun dışına tıklanınca kutu kapanır. */
      if (quickSizes.current && !event.target.closest('[data-card-sizes]')) quickSizes.close();
      return;
    }
    if (trigger.disabled) return;

    const mode = trigger.dataset.quickMode;
    if (mode === 'inline') {
      const panel = trigger.parentElement.querySelector('[data-card-sizes]');
      if (panel) {
        event.preventDefault();
        if (quickSizes.current && quickSizes.current.panel === panel) quickSizes.close(true);
        else quickSizes.open(trigger, panel);
        return;
      }
    }
    quickSizes.close();
    if (mode === 'direct' && trigger.dataset.variantId) {
      event.preventDefault();
      quickAddVariant(trigger, trigger, null);
      return;
    }

    /* Birden çok seçeneği olan ürün: seçim çekmecede yapılır. */
    const overlay = document.getElementById('QuickAddDrawer');
    if (!overlay) {
      window.location.assign(trigger.dataset.quickAdd);
      return;
    }
    event.preventDefault();
    const content = overlay.querySelector('[data-quick-add-content]');
    const url = new URL(trigger.dataset.quickAdd, window.location.origin);
    url.searchParams.set('section_id', 'quick-add');
    trigger.setAttribute('aria-busy', 'true');
    try {
      const doc = await theme.fetchDocument(url.toString());
      const source = doc.querySelector('quick-add');
      if (!source) throw new Error('Quick add markup missing');
      content.replaceChildren(source);
      overlay.open(trigger);
    } catch (error) {
      window.location.assign(trigger.dataset.quickAdd);
    } finally {
      trigger.removeAttribute('aria-busy');
    }
  });

  /* ------------------------------------------------------------------ */
  /* Sohbet (WhatsApp) penceresi                                          */
  /* ------------------------------------------------------------------ */

  class ChatWidget extends HTMLElement {
    connectedCallback() {
      this.panel = this.querySelector('.aceso-chat-panel');
      this.launcher = this.querySelector('.aceso-chat-launcher');
      this.welcome = this.querySelector('.aceso-chat-welcome');
      this.conversation = this.querySelector('.aceso-chat-conversation');
      this.input = this.querySelector('.aceso-chat-composer input');
      this.send = this.querySelector('.aceso-chat-composer button');
      if (!this.panel || !this.launcher) return;

      this.launcher.addEventListener('click', () => this.setOpen(true));
      this.querySelector('.aceso-chat-minimize')?.addEventListener('click', () => this.setOpen(false, true));
      this.querySelector('.aceso-chat-start')?.addEventListener('click', () => {
        if (this.welcome) this.welcome.hidden = true;
        if (this.conversation) this.conversation.hidden = false;
        this.input?.focus();
      });
      this.querySelectorAll('.aceso-chat-suggestions button').forEach((button) =>
        button.addEventListener('click', () => {
          if (!this.input) return;
          this.input.value = button.textContent.trim();
          this.updateSend();
          this.input.focus();
        }),
      );
      this.input?.addEventListener('input', () => this.updateSend());
      this.querySelector('.aceso-chat-composer')?.addEventListener('submit', (event) => {
        event.preventDefault();
        const text = this.input.value.trim();
        if (!text || !this.dataset.whatsapp) return;
        const separator = this.dataset.whatsapp.includes('?') ? '&' : '?';
        window.open(`${this.dataset.whatsapp}${separator}text=${encodeURIComponent(text)}`, '_blank', 'noopener,noreferrer');
        this.input.value = '';
        this.updateSend();
      });

      this.onKey = (event) => {
        if (event.key === 'Escape' && !this.panel.hidden) this.setOpen(false, true);
      };
      window.addEventListener('keydown', this.onKey);
    }

    disconnectedCallback() {
      window.removeEventListener('keydown', this.onKey);
    }

    updateSend() {
      if (this.send) this.send.disabled = !this.input.value.trim();
    }

    setOpen(open, restoreFocus = false) {
      this.classList.toggle('is-open', open);
      this.panel.hidden = !open;
      this.launcher.hidden = open;
      this.launcher.setAttribute('aria-expanded', String(open));
      if (open) this.querySelector('.aceso-chat-minimize')?.focus();
      else if (restoreFocus) this.launcher.focus();
    }
  }
  customElements.define('chat-widget', ChatWidget);

  /* ------------------------------------------------------------------ */
  /* Sayfa başına dön                                                     */
  /* ------------------------------------------------------------------ */

  /* Düğme yalnızca gösterildiğinde DOM'a eklenir: stiller sohbet düğmesini
     body:has(.back-to-top) ile yukarı kaydırır. */
  class BackToTop extends HTMLElement {
    connectedCallback() {
      this.template = this.querySelector('template');
      if (!this.template) return;
      this.onScroll = () => this.update();
      window.addEventListener('scroll', this.onScroll, { passive: true });
      this.update();
    }

    disconnectedCallback() {
      window.removeEventListener('scroll', this.onScroll);
    }

    update() {
      const show = window.scrollY > 900;
      const button = this.querySelector('.back-to-top');
      if (show && !button) {
        const created = this.template.content.firstElementChild.cloneNode(true);
        created.addEventListener('click', () =>
          window.scrollTo({ top: 0, behavior: theme.prefersReducedMotion() ? 'instant' : 'smooth' }),
        );
        this.append(created);
      } else if (!show && button) {
        button.remove();
      }
    }
  }
  customElements.define('back-to-top', BackToTop);

  /* ------------------------------------------------------------------ */
  /* Son incelenenler                                                     */
  /* ------------------------------------------------------------------ */

  const RECENT_KEY = 'aceso:recently-viewed';

  theme.recentlyViewed = {
    all() {
      const list = storage.read(RECENT_KEY, []);
      return Array.isArray(list) ? list.filter((item) => typeof item === 'string') : [];
    },
    push(handle) {
      const list = [handle, ...this.all().filter((item) => item !== handle)].slice(0, 8);
      storage.write(RECENT_KEY, list);
    },
  };

  class RecentlyViewed extends HTMLElement {
    connectedCallback() {
      this.load();
    }

    async load() {
      const current = this.dataset.currentHandle;
      const limit = Number(this.dataset.limit || 4);
      const handles = theme.recentlyViewed
        .all()
        .filter((handle) => handle !== current)
        .slice(0, limit);
      if (current) theme.recentlyViewed.push(current);
      if (!handles.length) return;
      const cards = (await Promise.all(handles.map((handle) => theme.fetchProductCard(handle)))).filter(Boolean);
      if (!cards.length) return;
      this.querySelector('[data-recently-viewed-grid]').replaceChildren(...cards);
      this.hidden = false;
      favorites.sync(this);
    }
  }
  customElements.define('recently-viewed', RecentlyViewed);

  /* ------------------------------------------------------------------ */
  /* Ürün önerileri                                                       */
  /* ------------------------------------------------------------------ */

  class ProductRecommendations extends HTMLElement {
    connectedCallback() {
      if (this.hasAttribute('data-loaded') || !this.dataset.url) return;
      const load = async () => {
        try {
          const doc = await theme.fetchDocument(this.dataset.url);
          const source = doc.querySelector('[data-recommendations-grid]');
          const grid = this.querySelector('[data-recommendations-grid]');
          if (source && grid && source.querySelector('product-card')) {
            grid.innerHTML = source.innerHTML;
            favorites.sync(grid);
          }
        } catch (error) {
          /* Öneri alınamazsa yedek liste yerinde kalır. */
        } finally {
          this.setAttribute('data-loaded', '');
          if (!this.querySelector('product-card')) this.hidden = true;
        }
      };
      if ('IntersectionObserver' in window) {
        this.observer = new IntersectionObserver(
          (entries) => {
            if (!entries[0].isIntersecting) return;
            this.observer.disconnect();
            load();
          },
          { rootMargin: '0px 0px 400px 0px' },
        );
        this.observer.observe(this);
      } else {
        load();
      }
    }

    disconnectedCallback() {
      this.observer?.disconnect();
    }
  }
  customElements.define('product-recommendations', ProductRecommendations);

  /* ------------------------------------------------------------------ */
  /* Dil / ülke seçici                                                    */
  /* ------------------------------------------------------------------ */

  class LocalizationForm extends HTMLElement {
    connectedCallback() {
      this.addEventListener('change', (event) => {
        if (event.target.matches('select')) this.querySelector('form')?.submit();
      });
    }
  }
  customElements.define('localization-form', LocalizationForm);

  /* ------------------------------------------------------------------ */
  /* Başlangıç                                                            */
  /* ------------------------------------------------------------------ */

  const init = () => favorites.sync();
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
  document.addEventListener('shopify:section:load', () => favorites.sync());
})();
